import React, { useState, useEffect, useMemo, useCallback } from 'react';
import type { VisibilityState } from '@tanstack/react-table';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  Box,
  Grid,
  Paper,
  Typography,
  Button,
  Tabs,
  Tab,
  Skeleton,
  TablePagination,
  alpha,
  Snackbar,
  Alert,
} from '@mui/material';
import { Add as AddIcon } from '@mui/icons-material';
import { SEARCE_COLORS } from '../theme';
import { customerRequestApi, CustomerRequestResponse, SolutionStoryResponse, SolutionStoryListItem, PublicTestimonialResponse, ProjectDropdownOption } from '../services/api';
import { CustomerRequestDetailModal } from '../components/customerSentiment/CustomerRequestDetailModal';
import { SolutionStoryModal } from '../components/customerSentiment/SolutionStoryModal';
import {
  PageHeader,
  MetricCard,
  FeedbackCard,
  EscalationCard,
  FilterMenu,
  FeedbackResponsesTable,
  MyProjectsTable,
  StandardTable,
  COLUMN_CONFIGS,
} from '../components/customerSentiment';
import CustomerSentimentTableToolbar from '../components/customerSentiment/CustomerSentimentTableToolbar';
import {
  visibleColumnConfigs,
} from '../components/customerSentiment/customerSentimentColumnWidths';
import {
  exportCustomerSentimentTableToCsv,
  hasVisibleExportColumns,
} from '../components/customerSentiment/customerSentimentCsvExport';
import {
  CurrentTabRow,
  FlattenedFeedbackResponse,
  ColumnConfig,
  extractProjectAccountBU,
  getLatestFeedbackRequestByProject,
  getFeedbackStatusLabel,
  canCreateMyProjectRequest,
  isEscalationResolutionClosed,
} from '../utils/customerSentimentutils';

// Main Component
const CustomerSentimentPage: React.FC = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState(0);
  
  // State for API data
  const [feedbackRequests, setFeedbackRequests] = useState<CustomerRequestResponse[]>([]);
  const [escalationRequests, setEscalationRequests] = useState<CustomerRequestResponse[]>([]);
  const [appreciationRequests, setAppreciationRequests] = useState<CustomerRequestResponse[]>([]);
  const [solutionStoryRequests, setSolutionStoryRequests] = useState<SolutionStoryListItem[]>([]);
  const [retryingEmailRequestId, setRetryingEmailRequestId] = useState<string | null>(null);
  /** Tracks in-flight Zoho retry: `requestId:scope` or `requestId:feedback_response:responseId` */
  const [retryingZohoKey, setRetryingZohoKey] = useState<string | null>(null);
  const [emailActionSnackbar, setEmailActionSnackbar] = useState<{
    open: boolean;
    message: string;
    severity: 'success' | 'error';
  }>({ open: false, message: '', severity: 'success' });
  const [publicTestimonialRequests, setPublicTestimonialRequests] = useState<PublicTestimonialResponse[]>([]);
  const [userProjects, setUserProjects] = useState<ProjectDropdownOption[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Compute flattened feedback responses
  const flattenedFeedbackResponses = useMemo<FlattenedFeedbackResponse[]>(() => {
    const responses: FlattenedFeedbackResponse[] = [];
    feedbackRequests.forEach((request) => {
      if (request.feedback_responses && request.feedback_responses.length > 0) {
        request.feedback_responses.forEach((response) => {
          // Determine feedback sentiment
          let feedbackSentiment = 'Pending';
          if (response.overall_satisfaction) {
            const satisfaction = response.overall_satisfaction.toLowerCase();
            if (satisfaction.includes('highly dissatisfied') || satisfaction.includes('dissatisfied')) {
              feedbackSentiment = 'Negative';
            } else if (satisfaction.includes('highly satisfied') || satisfaction.includes('satisfied') || satisfaction === 'neutral') {
              feedbackSentiment = 'Positive';
            }
          }

          responses.push({
            responseId: response.id,
            parentRequestId: request.id,
            requestNumber: request.request_number || null,
            date: response.submitted_at || request.created_at,
            projectCode: request.project_code || null,
            projectName: request.project_name || null,
            projectOwner: request.project_owner || null,
            customerName: response.customer_name || null,
            customerEmail: response.customer_email || null,
            account: response.account_name || request.account_name || null,
            region: extractProjectAccountBU(request.project_account_bu),
            overallSatisfaction: response.overall_satisfaction || null,
            feedbackSentiment,
            deliveryCompletion: response.delivery_completion_of_deliverables || null,
            deliveryTimeline: response.delivery_adherence_to_timeline || null,
            deliveryClosure: response.delivery_project_closure_handover || null,
            teamTechnical: response.team_technical_expertise || null,
            teamSupport: response.team_support_provided || null,
            teamResponsiveness: response.team_responsiveness || null,
            specificContributions: response.specific_contributions || null,
            areasForImprovement: response.areas_for_improvement || null,
            zohoStatus: response.zoho_status ?? null,
            zohoIssueLink: response.zoho_issue_link || request.zoho_issue_link || null,
            zohoIssueId: response.zoho_issue_id || request.zoho_issue_id || null,
          });
        });
      }
    });
    // Sort by date (newest first)
    return responses.sort((a, b) => {
      const dateA = new Date(a.date).getTime();
      const dateB = new Date(b.date).getTime();
      return dateB - dateA;
    });
  }, [feedbackRequests]);

  // Modal state
  const [selectedRequest, setSelectedRequest] = useState<CustomerRequestResponse | null>(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedSolutionStoryId, setSelectedSolutionStoryId] = useState<string | null>(null);
  const [solutionStoryModalOpen, setSolutionStoryModalOpen] = useState(false);

  // Pagination state
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  // Filter state
  const [filterMenus, setFilterMenus] = useState<{ [key: string]: { anchorEl: HTMLElement | null; selectedValues: string[] } }>({});
  const [searchTerms, setSearchTerms] = useState<{ [key: string]: string }>({});
  const [dateFilters, setDateFilters] = useState<{ [key: string]: { startDate: Date | null; endDate: Date | null } }>({});

  // Sort state
  const [sortColumn, setSortColumn] = useState<string | null>(null);
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('desc'); // Default: newest first

  const [columnVisibility, setColumnVisibility] = useState<VisibilityState>({});
  
  // Fetch data from API
  const fetchData = async () => {
    setLoading(true);
    setError(null);
    
    try {
      // Fetch main requests in parallel (solution story is handled separately to not affect other tabs)
      const [feedbackRes, escalationRes, appreciationRes, userProjectsRes] = await Promise.all([
        customerRequestApi.list({ request_type: 'feedback', page_size: 1000 }),
        customerRequestApi.list({ request_type: 'escalation', page_size: 1000 }),
        customerRequestApi.list({ request_type: 'appreciation', page_size: 1000 }),
        customerRequestApi.getUserProjects(),
      ]);
      
      setFeedbackRequests(feedbackRes.requests || []);
      setEscalationRequests(escalationRes.requests || []);
      setAppreciationRequests(appreciationRes.requests || []);
      setUserProjects(userProjectsRes.projects || []);
      
      // Debug: Check if closure_date is in the response
      if (feedbackRes.requests && feedbackRes.requests.length > 0) {
        console.log('All closure dates:', feedbackRes.requests.map(r => ({ 
          project_code: r.project_code, 
          closure_date: r.closure_date 
        })));
      }
    } catch (err) {
      console.error('Error fetching customer requests:', err);
      setError('Failed to fetch data. Please try again.');
    } finally {
      setLoading(false);
    }
    
    // Fetch solution stories separately - don't let failures affect other tabs
    try {
      const solutionStoryRes = await customerRequestApi.listSolutionStories({ page_size: 1000 });
      setSolutionStoryRequests(solutionStoryRes.requests || []);
    } catch (err) {
      console.error('Error fetching solution stories:', err);
      // Set empty array on error so the tab still works
      setSolutionStoryRequests([]);
      // Don't set global error - this is isolated to solution story tab
    }
    
    // Fetch public testimonials
    try {
      const publicTestimonialRes = await customerRequestApi.listPublicTestimonials({ page_size: 100 });
      setPublicTestimonialRequests(publicTestimonialRes.requests || []);
    } catch (err) {
      console.error('Error fetching public testimonials:', err);
      // Set empty array on error so the tab still works
      setPublicTestimonialRequests([]);
      // Don't set global error - this is isolated to public testimonial tab
    }
  };

  const handleRetryEmail = useCallback(async (row: SolutionStoryListItem | CustomerRequestResponse) => {
    if ('request_type' in row && row.request_type === 'feedback') {
      if (!row.id) return;
      setRetryingEmailRequestId(row.id);
      try {
        const res = await customerRequestApi.resendEmail(row.id);
        const fresh = await customerRequestApi.get(row.id);
        setFeedbackRequests((prev) => prev.map((r) => (r.id === row.id ? fresh : r)));
        setEmailActionSnackbar({
          open: true,
          message: res.success
            ? 'Notification email sent successfully.'
            : res.message || 'Email could not be sent. Please try again or contact support.',
          severity: res.success ? 'success' : 'error',
        });
      } catch (e: unknown) {
        let detail = 'Failed to retry email.';
        if (e && typeof e === 'object' && 'response' in e) {
          const data = (e as { response?: { data?: { detail?: unknown } } }).response?.data;
          if (data && typeof data.detail === 'string') {
            detail = data.detail;
          }
        }
        setEmailActionSnackbar({
          open: true,
          message: detail,
          severity: 'error',
        });
      } finally {
        setRetryingEmailRequestId(null);
      }
      return;
    }

    const storyRow = row as SolutionStoryListItem;
    if (!storyRow.id) return;
    setRetryingEmailRequestId(storyRow.id);
    try {
      const res = await customerRequestApi.retrySolutionStoryEmail(storyRow.id);
      setSolutionStoryRequests((prev) =>
        prev.map((r) => (r.id === storyRow.id ? { ...r, email_status: res.email_status } : r))
      );
      setEmailActionSnackbar({
        open: true,
        message:
          res.email_status === 'Success'
            ? 'Notification email sent successfully.'
            : 'Email could not be sent. Please try again or contact support.',
        severity: res.email_status === 'Success' ? 'success' : 'error',
      });
    } catch (e: unknown) {
      let detail = 'Failed to retry email.';
      if (e && typeof e === 'object' && 'response' in e) {
        const data = (e as { response?: { data?: { detail?: unknown } } }).response?.data;
        if (data && typeof data.detail === 'string') {
          detail = data.detail;
        }
      }
      setEmailActionSnackbar({
        open: true,
        message: detail,
        severity: 'error',
      });
    } finally {
      setRetryingEmailRequestId(null);
    }
  }, []);

  const handleRetryZoho = useCallback(
    async (
      requestId: string,
      body: {
        scope: 'escalation' | 'feedback_response' | 'feedback_failed_all';
        feedback_response_id?: string;
      }
    ) => {
      const key =
        body.scope === 'feedback_response' && body.feedback_response_id
          ? `${requestId}:feedback_response:${body.feedback_response_id}`
          : `${requestId}:${body.scope}`;
      setRetryingZohoKey(key);
      try {
        const fresh = await customerRequestApi.retryZoho(requestId, body);
        if (fresh.request_type === 'feedback') {
          setFeedbackRequests((prev) => prev.map((r) => (r.id === fresh.id ? fresh : r)));
        } else if (fresh.request_type === 'escalation') {
          setEscalationRequests((prev) => prev.map((r) => (r.id === fresh.id ? fresh : r)));
        }
        setSelectedRequest((prev) => (prev?.id === fresh.id ? fresh : prev));

        const stillFailed = (() => {
          if (fresh.request_type === 'escalation') {
            return Boolean(fresh.escalation_detail?.zoho_status?.includes('Failed'));
          }
          if (fresh.request_type !== 'feedback') return false;
          if (body.scope === 'feedback_response' && body.feedback_response_id) {
            const row = fresh.feedback_responses?.find((r) => r.id === body.feedback_response_id);
            return Boolean(row?.zoho_status?.includes('Failed'));
          }
          return Boolean(
            fresh.zoho_status?.includes('Failed') ||
              fresh.feedback_responses?.some((r) => r.zoho_status?.includes('Failed'))
          );
        })();

        setEmailActionSnackbar({
          open: true,
          message: stillFailed
            ? 'Zoho could not create the issue. Status is still failed — please try again or contact support.'
            : 'Zoho issue updated successfully.',
          severity: stillFailed ? 'error' : 'success',
        });
      } catch (e: unknown) {
        let detail = 'Could not retry Zoho issue.';
        if (e && typeof e === 'object' && 'response' in e) {
          const data = (e as { response?: { data?: { detail?: unknown } } }).response?.data;
          if (data && typeof data.detail === 'string') {
            detail = data.detail;
          }
        }
        setEmailActionSnackbar({
          open: true,
          message: detail,
          severity: 'error',
        });
      } finally {
        setRetryingZohoKey(null);
      }
    },
    []
  );

  // Get URL search params for deep linking
  const [searchParams, setSearchParams] = useSearchParams();
  
  // Fetch data on mount
  useEffect(() => {
    fetchData();
  }, []);
  
  // Check for requestId in URL and open modal after data is loaded
  useEffect(() => {
    const requestId = searchParams.get('requestId');
    const requestType = searchParams.get('type');
    
    if (requestId && !loading) {
      // Find the request in the appropriate list based on type
      let requestToOpen: CustomerRequestResponse | null = null;
      
      if (requestType === 'appreciation') {
        requestToOpen = appreciationRequests.find(r => r.id === requestId) || null;
      } else if (requestType === 'escalation') {
        requestToOpen = escalationRequests.find(r => r.id === requestId) || null;
      } else if (requestType === 'feedback') {
        requestToOpen = feedbackRequests.find(r => r.id === requestId) || null;
      } else if (requestType === 'solution-story') {
        const solutionStory = solutionStoryRequests.find(r => r.id === requestId);
        if (solutionStory) {
          setSelectedSolutionStoryId(solutionStory.id);
          setSolutionStoryModalOpen(true);
          if (activeTab !== 5) {
            setActiveTab(5);
          }
          // Clean up URL parameter after opening modal
          searchParams.delete('requestId');
          searchParams.delete('type');
          setSearchParams(searchParams, { replace: true });
          return;
        }
        requestToOpen = null;
      } else {
        // If no type specified, search all lists (excluding solution stories)
        requestToOpen = feedbackRequests.find(r => r.id === requestId) ||
                       escalationRequests.find(r => r.id === requestId) ||
                       appreciationRequests.find(r => r.id === requestId) ||
                       null;
        // Check solution stories separately
        const solutionStory = solutionStoryRequests.find(r => r.id === requestId);
        if (solutionStory) {
          setSelectedSolutionStoryId(solutionStory.id);
          setSolutionStoryModalOpen(true);
          if (activeTab !== 5) {
            setActiveTab(5);
          }
          // Clean up URL parameter after opening modal
          searchParams.delete('requestId');
          searchParams.delete('type');
          setSearchParams(searchParams, { replace: true });
          return;
        }
      }
      
      if (requestToOpen) {
        setSelectedRequest(requestToOpen);
        setModalOpen(true);
        // Switch to the appropriate tab if needed
        // Tab indices: 0=My Projects, 1=Feedback Requests, 2=Feedback Responses, 3=Escalations, 4=Appreciations, 5=Solution Stories, 6=Public Testimonials
        if (requestType === 'appreciation' && activeTab !== 4) {
          setActiveTab(4);
        } else if (requestType === 'escalation' && activeTab !== 3) {
          setActiveTab(3);
        } else if (requestType === 'feedback' && activeTab !== 1) {
          setActiveTab(1);
        }
        // Clean up URL parameter after opening modal
        searchParams.delete('requestId');
        searchParams.delete('type');
        setSearchParams(searchParams, { replace: true });
      }
    }
  }, [searchParams, loading, feedbackRequests, escalationRequests, appreciationRequests, setSearchParams, activeTab]);
  
  // Calculate analytics from real data
  const calculateAnalytics = () => {
    // Count total feedback responses from flattenedFeedbackResponses table
    const totalFeedback = flattenedFeedbackResponses.length;
    
    // Count feedback by sentiment from Feedback Responses table's "Feedback Sentiment" column
    let positiveFeedback = 0;
    let negativeFeedback = 0;
    let neutralFeedback = 0;
    
    flattenedFeedbackResponses.forEach(response => {
      const sentiment = response.feedbackSentiment?.toLowerCase() || '';
      if (sentiment === 'positive') {
        positiveFeedback++;
      } else if (sentiment === 'negative') {
          negativeFeedback++;
        } else {
        // Pending or other - count as neutral/awaiting
        neutralFeedback++;
      }
    });
    
    const openEscalations = escalationRequests.filter(
      req => !isEscalationResolutionClosed(req)
    ).length;
    const closedEscalations = escalationRequests.filter(
      req => isEscalationResolutionClosed(req)
    ).length;
    
    // Count pending reviews (feedback awaiting response)
    const pendingReviews = feedbackRequests.filter(
      req => req.status === 'email_sent' || req.status === 'pending'
    ).length;
    
    return {
      totalFeedback,
      positiveFeedback,
      negativeFeedback,
      neutralFeedback,
      totalAppreciations: appreciationRequests.length,
      totalEscalations: escalationRequests.length,
      openEscalations,
      closedEscalations,
      unresolvedEscalations: openEscalations,
      pendingReviews,
    };
  };
  
  const analytics = calculateAnalytics();

  const latestFeedbackRequestByProject = useMemo(
    () => getLatestFeedbackRequestByProject(feedbackRequests),
    [feedbackRequests]
  );

  const myProjectRows = useMemo(() => {
    return userProjects.map((project) => {
      const key = project.project_code?.trim();
      const latestRequest = key ? latestFeedbackRequestByProject.get(key) : undefined;
      const solutionStoryStatus = project.solution_story_status || 'Pending';
      const hasFeedbackRequest = Boolean(latestRequest);
      return {
        project_code: project.project_code,
        project_name: project.project_name,
        account: project.account ?? null,
        closure_date: project.closure_date,
        project_account_bu: project.project_account_bu,
        status: project.status,
        feedback_status: getFeedbackStatusLabel(latestRequest),
        solution_story_status: solutionStoryStatus,
        can_create_request: canCreateMyProjectRequest(hasFeedbackRequest, solutionStoryStatus),
      };
    });
  }, [userProjects, latestFeedbackRequestByProject]);

  const handleTabChange = (_event: React.SyntheticEvent, newValue: number) => {
    setActiveTab(newValue);
    setPage(0); // Reset to first page when switching tabs
    setSortColumn(null);
    setSortDirection('desc');
  };

  const handleNewRequest = () => {
    navigate('/customer-feedback');
  };

  // Get current tab's data (tab order: 0=My Projects, 1=Feedback Requests, 2=Feedback Responses, 3=Escalations, 4=Appreciations, 5=Solution Stories, 6=Public Testimonials)
  const getCurrentData = useCallback((): CurrentTabRow[] => {
    switch (activeTab) {
      case 0:
        return myProjectRows as Record<string, unknown>[];
      case 1:
        return feedbackRequests;
      case 2:
        return flattenedFeedbackResponses as any;
      case 3:
        return escalationRequests;
      case 4:
        return appreciationRequests;
      case 5:
        return solutionStoryRequests as any;
      case 6:
        return publicTestimonialRequests as any;
      default:
        return myProjectRows as Record<string, unknown>[];
    }
  }, [activeTab, feedbackRequests, escalationRequests, appreciationRequests, solutionStoryRequests, publicTestimonialRequests, flattenedFeedbackResponses, myProjectRows]);

  useEffect(() => {
    setColumnVisibility({});
    setPage(0);
  }, [activeTab]);

  // Get current tab's column config
  const getCurrentColumns = useCallback((): ColumnConfig[] => {
    switch (activeTab) {
      case 0:
        return COLUMN_CONFIGS.myProjects;
      case 1:
        return COLUMN_CONFIGS.feedback;
      case 2:
        return COLUMN_CONFIGS.feedbackResponses;
      case 3:
        return COLUMN_CONFIGS.escalation;
      case 4:
        return COLUMN_CONFIGS.appreciation;
      case 5:
        return COLUMN_CONFIGS.solutionStory;
      case 6:
        return COLUMN_CONFIGS.publicTestimonial;
      default:
        return COLUMN_CONFIGS.myProjects;
    }
  }, [activeTab]);

  const visibleColumns = useMemo(
    () => visibleColumnConfigs(getCurrentColumns(), columnVisibility, activeTab),
    [getCurrentColumns, columnVisibility, activeTab],
  );

  const exportFilenameBase = useMemo(() => {
    const bases = [
      'customer-sentiment-my-projects',
      'customer-sentiment-sign-off-requests',
      'customer-sentiment-sign-off-responses',
      'customer-sentiment-escalations',
      'customer-sentiment-appreciations',
      'customer-sentiment-solution-stories',
      'customer-sentiment-public-testimonials',
    ];
    return bases[activeTab] ?? 'customer-sentiment';
  }, [activeTab]);

  // Filter handlers
  const handleFilterClick = (columnId: string, event: React.MouseEvent<HTMLElement>) => {
    setFilterMenus(prev => ({
      ...prev,
      [columnId]: {
        anchorEl: event.currentTarget,
        selectedValues: prev[columnId]?.selectedValues || []
      }
    }));
  };

  const handleFilterClose = (columnId: string) => {
    setFilterMenus(prev => ({
      ...prev,
      [columnId]: {
        ...prev[columnId],
        anchorEl: null
      }
    }));
  };

  const handleClearAllFilters = () => {
    setFilterMenus({});
    setSearchTerms({});
    setDateFilters({});
  };

  // Get unique values for a column
  const getUniqueValues = (columnId: string, data: CurrentTabRow[]): string[] => {
    const columns = getCurrentColumns();
    const column = columns.find(c => c.id === columnId);
    if (!column) return [];
    
    const values = data
      .map(req => {
        const value = column.field(req as any);
        return value ? String(value) : null;
      })
      .filter((v): v is string => v !== null);
    
    return Array.from(new Set(values)).sort();
  };

  /** Rows matching all column filters except `excludeColumnId` (faceted filter options). */
  const getFacetedRowsForColumnOptions = useCallback(
    (excludeColumnId: string): CurrentTabRow[] => {
      const data = [...getCurrentData()];
      const columns = getCurrentColumns();
      return data.filter((req) =>
        columns.every((column) => {
          if (column.id === excludeColumnId) return true;
          const value = column.field(req as any);
          const stringValue = value ? String(value).toLowerCase() : '';

          if (column.isDate) {
            const dateFilter = dateFilters[column.id];
            if (dateFilter?.startDate || dateFilter?.endDate) {
              const reqDate = value ? new Date(value as string | number | Date) : null;
              if (!reqDate || isNaN(reqDate.getTime())) return false;

              if (dateFilter.startDate && reqDate < dateFilter.startDate) return false;
              if (dateFilter.endDate) {
                const endDate = new Date(dateFilter.endDate);
                endDate.setHours(23, 59, 59, 999);
                if (reqDate > endDate) return false;
              }
            }
            return true;
          }

          const selectedValues = filterMenus[column.id]?.selectedValues || [];
          const searchTerm = searchTerms[column.id]?.toLowerCase() || '';

          if (selectedValues.length > 0) {
            const matches = selectedValues.some((selected) => stringValue === selected.toLowerCase());
            if (!matches) return false;
          }

          if (searchTerm) {
            if (!stringValue.includes(searchTerm)) return false;
          }

          return true;
        })
      );
    },
    [getCurrentData, getCurrentColumns, filterMenus, searchTerms, dateFilters]
  );

  // Check if filter is active
  const isFilterActive = (columnId: string): boolean => {
    const columns = getCurrentColumns();
    const column = columns.find(c => c.id === columnId);
    if (!column) return false;
    
    if (column.isDate) {
      const dateFilter = dateFilters[columnId];
      return dateFilter?.startDate != null || dateFilter?.endDate != null;
    }
    
    return filterMenus[columnId]?.selectedValues.length > 0 || 
           (searchTerms[columnId]?.trim().length || 0) > 0;
  };

  const hasAnyActiveFilters = (): boolean => {
    return Object.keys(filterMenus).some(key => filterMenus[key]?.selectedValues.length > 0) ||
           Object.keys(searchTerms).some(key => (searchTerms[key]?.trim().length || 0) > 0) ||
           Object.keys(dateFilters).some(key => dateFilters[key]?.startDate != null || dateFilters[key]?.endDate != null);
  };

  // Filtered and sorted data
  const filteredAndSortedData = useMemo(() => {
    let data = [...getCurrentData()];
    const columns = getCurrentColumns();

    // Apply filters
    data = data.filter(req => {
      return columns.every(column => {
        const value = column.field(req);
        const stringValue = value ? String(value).toLowerCase() : '';

        // Date filter
        if (column.isDate) {
          const dateFilter = dateFilters[column.id];
          if (dateFilter?.startDate || dateFilter?.endDate) {
            const reqDate = value ? new Date(value) : null;
            if (!reqDate || isNaN(reqDate.getTime())) return false;
            
            if (dateFilter.startDate && reqDate < dateFilter.startDate) return false;
            if (dateFilter.endDate) {
              const endDate = new Date(dateFilter.endDate);
              endDate.setHours(23, 59, 59, 999);
              if (reqDate > endDate) return false;
            }
          }
          return true;
        }

        // Text filter
        const selectedValues = filterMenus[column.id]?.selectedValues || [];
        const searchTerm = searchTerms[column.id]?.toLowerCase() || '';

        if (selectedValues.length > 0) {
          const matches = selectedValues.some(selected => 
            stringValue === selected.toLowerCase()
          );
          if (!matches) return false;
        }

        if (searchTerm) {
          if (!stringValue.includes(searchTerm)) return false;
        }

        return true;
      });
    });

    // Apply sorting
    if (sortColumn) {
      const column = columns.find(c => c.id === sortColumn);
      if (column) {
        data.sort((a, b) => {
          const aValue = column.field(a as any);
          const bValue = column.field(b as any);
          
          if (aValue === null && bValue === null) return 0;
          if (aValue === null) return 1;
          if (bValue === null) return -1;

          if (column.isDate) {
            const aDate = new Date(aValue);
            const bDate = new Date(bValue);
            return sortDirection === 'asc' 
              ? aDate.getTime() - bDate.getTime()
              : bDate.getTime() - aDate.getTime();
          }

          const comparison = String(aValue).localeCompare(String(bValue));
          return sortDirection === 'asc' ? comparison : -comparison;
        });
      }
    } else {
      // Default sort by date (newest first)
      const dateColumn = columns.find(c => c.isDate);
      if (dateColumn) {
        data.sort((a, b) => {
          const aValue = dateColumn.field(a as any);
          const bValue = dateColumn.field(b as any);
          if (!aValue || !bValue) return 0;
          const aDate = new Date(aValue);
          const bDate = new Date(bValue);
          return bDate.getTime() - aDate.getTime();
        });
      }
    }

    return data;
  }, [getCurrentData, getCurrentColumns, filterMenus, searchTerms, dateFilters, sortColumn, sortDirection]);

  const handleExportTable = useCallback(() => {
    exportCustomerSentimentTableToCsv({
      rows: filteredAndSortedData,
      columns: getCurrentColumns(),
      columnVisibility,
      filenameBase: exportFilenameBase,
    });
  }, [filteredAndSortedData, getCurrentColumns, columnVisibility, exportFilenameBase]);

  // Paginated data
  const paginatedData = useMemo(() => {
    const startIndex = page * rowsPerPage;
    return filteredAndSortedData.slice(startIndex, startIndex + rowsPerPage);
  }, [filteredAndSortedData, page, rowsPerPage]);

  // Sort handler
  const handleSort = (columnId: string) => {
    if (sortColumn === columnId) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortColumn(columnId);
      setSortDirection('asc');
    }
    setPage(0);
  };

  // Pagination handlers
  const handleChangePage = (_event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  // Modal handlers
  const handleViewClick = (request: CustomerRequestResponse | SolutionStoryListItem) => {
    // Check if it's a solution story by checking if it exists in solutionStoryRequests
    // Solution stories have request_number starting with "SOL-"
    const isSolutionStory = solutionStoryRequests.some(ss => ss.id === request.id) || 
                            (request.request_number && request.request_number.startsWith('SOL-'));
    
    // Public testimonials (TES- prefix) should not open a modal - all data is shown in table
    const isPublicTestimonial = request.request_number && request.request_number.startsWith('TES-');
    if (isPublicTestimonial) {
      return; // Do nothing - no modal for public testimonials
    }
    
    if (isSolutionStory) {
      const ss = solutionStoryRequests.find((s) => s.id === request.id);
      const st = ss?.status || '';
      if (st === 'Draft' || st === 'Failed' || st === 'Submitting') {
        navigate(`/customer-feedback?type=solution-story&draftId=${request.id}`);
        return;
      }
      setSelectedSolutionStoryId(request.id);
      setSolutionStoryModalOpen(true);
    } else {
      setSelectedRequest(request as CustomerRequestResponse);
      setModalOpen(true);
    }
  };

  const handleCloseModal = () => {
    setModalOpen(false);
    setSelectedRequest(null);
  };

  const handleCloseSolutionStoryModal = () => {
    setSolutionStoryModalOpen(false);
    setSelectedSolutionStoryId(null);
  };

  // Render filter menu
  const renderFilterMenu = (columnId: string, columnLabel: string) => {
    const menuData = filterMenus[columnId];
    const columns = getCurrentColumns();
    const column = columns.find(c => c.id === columnId);
    if (!column) return null;

    const uniqueValues = getUniqueValues(columnId, getFacetedRowsForColumnOptions(columnId));
    const searchTerm = searchTerms[columnId] || '';
    const dateFilter = dateFilters[columnId] || { startDate: null, endDate: null };

    const handleClearDateFilter = () => {
      setDateFilters(prev => ({
        ...prev,
        [columnId]: { startDate: null, endDate: null }
      }));
    };

    return (
      <FilterMenu
        columnId={columnId}
        columnLabel={columnLabel}
        column={column}
        anchorEl={menuData?.anchorEl || null}
        onClose={() => handleFilterClose(columnId)}
        uniqueValues={uniqueValues}
        searchTerm={searchTerm}
        selectedValues={filterMenus[columnId]?.selectedValues || []}
        onCommitTextFilter={(values, inMenuSearch) => {
          setFilterMenus(prev => ({
            ...prev,
            [columnId]: { ...prev[columnId], selectedValues: values },
          }));
          setSearchTerms(prev => ({
            ...prev,
            [columnId]: inMenuSearch,
          }));
        }}
        onClearTextFilter={() => {
          setFilterMenus(prev => ({
            ...prev,
            [columnId]: { ...prev[columnId], selectedValues: [] },
          }));
          setSearchTerms(prev => {
            const newTerms = { ...prev };
            delete newTerms[columnId];
            return newTerms;
          });
        }}
        dateFilter={dateFilter}
        onDateFilterApply={(startDate, endDate) => {
          setDateFilters(prev => ({
            ...prev,
            [columnId]: { startDate, endDate },
          }));
        }}
        onClearDateFilter={handleClearDateFilter}
      />
    );
  };

  return (
    <Box sx={{ px: { xs: 2, sm: 3, md: 4 }, pt: 0, pb: 3 }}>
      {/* Page Header */}
      <PageHeader
        title="Client Sentiment"
        subtitle="Track and analyze Submit Customer Sign-Off, appreciations, and escalations across your organization. Monitor sentiment trends and identify areas for improvement."
        actionButton={
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={handleNewRequest}
            sx={{
              backgroundColor: SEARCE_COLORS.primary,
              color: SEARCE_COLORS.white,
              textTransform: 'none',
              fontWeight: 600,
              px: 3,
              py: 1,
              borderRadius: 2,
              boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
              '&:hover': {
                backgroundColor: SEARCE_COLORS.primaryDark || SEARCE_COLORS.secondary,
                boxShadow: '0 4px 12px rgba(0,0,0,0.2)',
              },
            }}
          >
            New Request
          </Button>
        }
      />

      {/* Key Metrics Cards - 3 Cards in a Row */}
      <Grid container spacing={3} sx={{ mb: 5 }}>
        <Grid item xs={12} sm={6} md={4}>
          {loading ? (
            <Skeleton variant="rectangular" height={120} sx={{ borderRadius: 2 }} />
          ) : (
            <FeedbackCard
              total={analytics.totalFeedback}
              positive={analytics.positiveFeedback}
              negative={analytics.negativeFeedback}
              color={SEARCE_COLORS.turquoise}
              tooltip="Customer feedback submissions in scope: total count with positive vs negative sentiment breakdown."
            />
          )}
        </Grid>
        <Grid item xs={12} sm={6} md={4}>
          {loading ? (
            <Skeleton variant="rectangular" height={120} sx={{ borderRadius: 2 }} />
          ) : (
            <EscalationCard
              total={analytics.totalEscalations}
              open={analytics.openEscalations}
              closed={analytics.closedEscalations}
              color={SEARCE_COLORS.error}
              tooltip="Escalation records in scope: total escalations with open vs closed status."
            />
          )}
        </Grid>
        <Grid item xs={12} sm={6} md={4}>
          {loading ? (
            <Skeleton variant="rectangular" height={120} sx={{ borderRadius: 2 }} />
          ) : (
            <MetricCard
              title="Total Appreciations"
              value={analytics.totalAppreciations}
              color={SEARCE_COLORS.secondary}
              tooltip="Total appreciation submissions recorded for the current filters and period on this page."
            />
          )}
        </Grid>
      </Grid>

      {/* Feedback & Escalations Table Section */}
      <Typography
        variant="h6"
        sx={{
          fontWeight: 600,
          color: SEARCE_COLORS.textPrimary,
          mb: 3,
        }}
      >
        Feedback & Escalations Details
      </Typography>

      <Paper
        elevation={0}
        sx={{
          overflow: 'hidden',
          border: `1px solid ${SEARCE_COLORS.border}`,
          borderRadius: 2,
        }}
      >
        {/* Tabs - scrollable when window is small */}
        <Box sx={{ borderBottom: `1px solid ${SEARCE_COLORS.border}`, overflowX: 'auto' }}>
          <Tabs
            value={activeTab}
            onChange={handleTabChange}
            variant="scrollable"
            scrollButtons="auto"
            allowScrollButtonsMobile
            sx={{
              px: 3,
              minHeight: 48,
              '& .MuiTab-root': {
                textTransform: 'none',
                fontWeight: 500,
                fontSize: '0.875rem',
                color: SEARCE_COLORS.textSecondary,
                minHeight: 48,
                px: 2,
                flexShrink: 0,
                '&.Mui-selected': {
                  color: SEARCE_COLORS.primary,
                },
              },
              '& .MuiTabs-indicator': {
                backgroundColor: SEARCE_COLORS.primary,
                height: 3,
              },
              '& .MuiTabs-scrollButtons': {
                color: SEARCE_COLORS.primary,
                '&.Mui-disabled': {
                  opacity: 0.3,
                },
              },
            }}
          >
            <Tab label={`My Projects (${userProjects.length})`} />
            <Tab label={`Sign-Off Requests (${feedbackRequests.length})`} />
            <Tab label={`Sign-Off Responses (${flattenedFeedbackResponses.length})`} />
            <Tab label={`Escalations (${escalationRequests.length})`} />
            <Tab label={`Appreciations (${appreciationRequests.length})`} />
            <Tab label={`Solution Stories(${solutionStoryRequests.length})`} />
            <Tab label={`Public Testimonials(${publicTestimonialRequests.length})`} />
          </Tabs>
        </Box>

        <CustomerSentimentTableToolbar
          columns={getCurrentColumns()}
          columnVisibility={columnVisibility}
          onColumnVisibilityChange={setColumnVisibility}
          onExport={handleExportTable}
          exportDisabled={
            filteredAndSortedData.length === 0 ||
            !hasVisibleExportColumns(getCurrentColumns(), columnVisibility)
          }
          showClearFilters={hasAnyActiveFilters()}
          onClearAllFilters={handleClearAllFilters}
        />

        {/* Table */}
        {activeTab === 2 ? (
          <FeedbackResponsesTable
            columns={visibleColumns}
            data={filteredAndSortedData as FlattenedFeedbackResponse[]}
            loading={loading}
            error={error}
            page={page}
            rowsPerPage={rowsPerPage}
            onPageChange={(_, newPage) => setPage(newPage)}
            onRowsPerPageChange={(e) => {
              setRowsPerPage(parseInt(e.target.value, 10));
              setPage(0);
            }}
            onSort={handleSort}
            onFilterClick={handleFilterClick}
            isFilterActive={isFilterActive}
            renderFilterMenu={renderFilterMenu}
            feedbackRequests={feedbackRequests}
            onViewClick={handleViewClick}
            onRetryZoho={(parentRequestId: string, feedbackResponseId: string) =>
              handleRetryZoho(parentRequestId, {
                scope: 'feedback_response',
                feedback_response_id: feedbackResponseId,
              })
            }
            retryingZohoKey={retryingZohoKey}
          />
        ) : activeTab === 0 ? (
          <MyProjectsTable
            columns={visibleColumns}
            data={paginatedData}
            loading={loading}
            error={error}
            onSort={handleSort}
            onFilterClick={handleFilterClick}
            isFilterActive={isFilterActive}
            renderFilterMenu={renderFilterMenu}
            onCreateRequest={handleNewRequest}
          />
        ) : (
          <>
            <StandardTable
              columns={visibleColumns}
              data={paginatedData}
              loading={loading}
              error={error}
              activeTab={activeTab}
              onSort={handleSort}
              onFilterClick={handleFilterClick}
              isFilterActive={isFilterActive}
              renderFilterMenu={renderFilterMenu}
              onViewClick={handleViewClick}
              onRetryEmail={handleRetryEmail}
              retryingEmailRequestId={retryingEmailRequestId}
              onRetryZoho={(req, scope) => handleRetryZoho(req.id, { scope })}
              retryingZohoKey={retryingZohoKey}
            />
            <TablePagination
              rowsPerPageOptions={[10, 25, 50, 100]}
              component="div"
              count={filteredAndSortedData.length}
              rowsPerPage={rowsPerPage}
              page={page}
              onPageChange={handleChangePage}
              onRowsPerPageChange={handleChangeRowsPerPage}
              sx={{
                '& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows': {
                  color: SEARCE_COLORS.textSecondary,
                  fontWeight: 500,
                },
                '& .MuiTablePagination-select': {
                  color: SEARCE_COLORS.primary,
                  fontWeight: 600,
                },
                '& .MuiIconButton-root': {
                  color: SEARCE_COLORS.primary,
                  '&:hover': {
                    backgroundColor: alpha(SEARCE_COLORS.primary, 0.1),
                  },
                  '&.Mui-disabled': {
                    color: SEARCE_COLORS.gray_400,
                  },
                },
              }}
            />
          </>
        )}
      </Paper>

      <Snackbar
        open={emailActionSnackbar.open}
        autoHideDuration={6000}
        onClose={() => setEmailActionSnackbar((s) => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          severity={emailActionSnackbar.severity}
          onClose={() => setEmailActionSnackbar((s) => ({ ...s, open: false }))}
          sx={{ width: '100%' }}
        >
          {emailActionSnackbar.message}
        </Alert>
      </Snackbar>

      {/* Detail Modal */}
      <CustomerRequestDetailModal
        open={modalOpen}
        onClose={handleCloseModal}
        request={selectedRequest}
        loading={false}
      />
      <SolutionStoryModal
        open={solutionStoryModalOpen}
        onClose={handleCloseSolutionStoryModal}
        storyId={selectedSolutionStoryId}
        loading={false}
      />
    </Box>
  );
};

export default CustomerSentimentPage;
